import { Button, Text } from "@solomon-labs/design/components/ui";
import React, { useState } from "react";

import { List } from "../../../components/Data/List";
import { Section } from "../../../components/Section";

import { PolicyCreator } from "./PolicyCreator";
import { POLICY_CATALOG } from "./policyFixtures";
import { PolicyRow } from "./PolicyRow";
import type { Policy, ProgramConfigStatus } from "./types";

/** The exact subset of the app `Program` the Policies View reads — just the
 *  status gate (draft ⇒ editable, active/ended ⇒ read-only). Typed as a subset
 *  so the app can keep passing its real `Program` straight through the shim. */
export interface ProgramPoliciesProgram {
    status: ProgramConfigStatus;
}

/** Sim-only forced-state seam for the read-path Policies list — targets ONE
 *  attached policy's Edit affordance for a scripted cursor hover-tour.
 *  Optional; absent in production (the list is fully inert). */
export interface ProgramPoliciesDemo {
    /** The policy id whose Edit button should paint its forced hover, or null. */
    editHoverId: string | null;
    /** Cursor-key prefix applied to each row's Edit button (`<prefix>-<id>`)
     *  so the sim's `moveTo` can travel to a row's Edit affordance. */
    editCursorKeyPrefix: string;
}

export interface ProgramPoliciesProps {
    program: ProgramPoliciesProgram;
    /** Attached policies + their values — lifted in ProgramDetail because they
     *  feed the BuilderConfig assembled at the confirm CTA. */
    policies: Policy[];
    policyValues: Record<string, number | string>;
    /** Attach a newly-created policy (draft only). */
    onAddPolicy: (policy: Policy, value: number | string) => void;
    /** Detach a policy (draft only). */
    onRemovePolicy: (id: string) => void;
    /** Edit an attached policy's value in place (active / ended only). */
    onChangePolicyValue: (id: string, value: number | string) => void;
    /** Sim-only cursor-tour seam over the read-path rows. Inert (undefined) in
     *  production. */
    demo?: ProgramPoliciesDemo;
}

/**
 * ProgramPolicies — the Policies section of the program detail. A Domain
 * Component that derives its mode from `program.status` (draft ⇒ creation,
 * active/ended ⇒ created) and renders the matching body into a
 * `<Section heading="Policies">` with the "+Create Policy" action in the
 * header (draft only):
 * - **creation** — the create-policy editor (opened by +Create Policy, via
 *   `PolicyCreator`) + the attached-policy list, each row removable.
 * - **created** — the attached-policy list only, each row value-editable in
 *   place.
 *
 * The DC owns the editor open/close UI state (`creating`); the `policies` /
 * `policyValues` and their mutators are props because they live in
 * ProgramDetail (they feed the BuilderConfig).
 *
 * Hoisted into `shared/widgets/yaas/programs` so external consumers (the
 * "+New Program" wiki doc) can render the bare Policies section fixture-driven,
 * with no wallet / RPC / orgApiFetch. The app keeps a re-export shim at the old
 * path so `yaas-client` compiles unchanged.
 */
export function ProgramPolicies({
    program,
    policies,
    policyValues,
    onAddPolicy,
    onRemovePolicy,
    onChangePolicyValue,
    demo
}: ProgramPoliciesProps): React.ReactElement {
    // draft (in_progress) ⇒ editable; active / ended ⇒ read-only.
    const creation = program.status === "in_progress";

    const [creating, setCreating] = useState(false);

    // Policy templates the program doesn't already have — the inline
    // "+ Create Policy" editor picks from these.
    const available = POLICY_CATALOG.filter(
        (candidate) => !policies.some((p) => p.id === candidate.id)
    );

    function addPolicy(policy: Policy, value: number | string): void {
        onAddPolicy(policy, value);
        setCreating(false);
    }

    const action = creation ? (
        <Button
            variant="sunken"
            size="sm"
            onClick={() => setCreating((c) => !c)}
            disabled={available.length === 0}
        >
            +Create Policy
        </Button>
    ) : undefined;

    return (
        <Section
            heading="Policies"
            className="pt-8"
            headerClassName="px-4 mb-6"
            action={action}
        >
            {/* Create-policy editor — nested raised card directly under the
                header. This is the generic editor (type selector + "Create
                policy" title) opened by the +Create Policy button. */}
            {creating && available.length > 0 && (
                <div className="px-4 mb-4">
                    <PolicyCreator
                        available={available}
                        onAdd={addPolicy}
                        onCancel={() => setCreating(false)}
                    />
                </div>
            )}
            {creation && policies.length > 0 && (
                <Text
                    variant="fine-print"
                    render={<span />}
                    className="text-foreground-muted block mt-6 mb-1 px-4"
                >
                    Added
                </Text>
            )}
            <List>
                {policies.map((policy) => (
                    <PolicyRow
                        key={policy.id}
                        policy={policy}
                        value={policyValues[policy.id]}
                        onRemove={
                            creation
                                ? (): void => onRemovePolicy(policy.id)
                                : undefined
                        }
                        onChangeValue={
                            creation
                                ? undefined
                                : (v): void => onChangePolicyValue(policy.id, v)
                        }
                        editCursorKey={
                            demo
                                ? `${demo.editCursorKeyPrefix}-${policy.id}`
                                : undefined
                        }
                        demo={
                            demo?.editHoverId === policy.id
                                ? { editPhase: "hover", rowHover: true }
                                : undefined
                        }
                    />
                ))}
            </List>
        </Section>
    );
}
